import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import './Dropdown.css'; // Certifique-se de que o CSS compartilhado está configurado corretamente
import PopupNovaOportunidade from './PopupNovaOportunidade';
import PopupEditarOportunidade from './PopupEditarOportunidade';
import LoadingOverlay from '../components/LoadingOverlay';
import { getAuthToken } from '../utils/auth';
import BASE_URL from '../utils/api';

// Helper: listens for MobileFooter generic + event
function MobileAddListener({ onOpen }) {
  useEffect(() => {
    window.addEventListener('open-mobile-add-popup', onOpen);
    return () => window.removeEventListener('open-mobile-add-popup', onOpen);
  }, [onOpen]);
  return null;
}

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

function OportunidadeDropdown() {
  const query = useQuery();
  const location = useLocation();
  const idCliente = query.get('idCliente');

  const [oportunidades, setOportunidades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtro, setFiltro] = useState('clienteId'); // Estado para o tipo de filtro
  const [valorFiltro, setValorFiltro] = useState(''); // Estado para o valor do filtro
  const [oportunidadesFiltradas, setOportunidadesFiltradas] = useState(oportunidades); // Estado para as oportunidades filtradas
  const [dropdownAberto, setDropdownAberto] = useState(false); // Estado para controlar o dropdown de filtro
  const [mostrarPopupCliente, setMostrarPopupCliente] = useState(false);
  const [clientes, setClientes] = useState([]);
  const [popupNovaOportunidade, setPopupNovaOportunidade] = useState(null);
  const [popupEditarOportunidade, setPopupEditarOportunidade] = useState(null);

  // Auto-abrir formulário de criação quando navegado de outra página com cliente pré-selecionado
  useEffect(() => {
    if (location.state?.autoOpenForm && location.state?.clientePreSelecionado) {
      const cliente = location.state.clientePreSelecionado;
      setPopupNovaOportunidade(cliente);
      // Limpar o state para não reabrir ao navegar de volta
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Função para fazer chamadas à API com fallback de URLs (removendo portas antigas 44371 e 64918)
  const apiCall = async (endpoint) => {
    const urls = [
      `${BASE_URL}/${endpoint}`,
      `${BASE_URL}/${endpoint}`
    ];

    const headers = {};
    const token = getAuthToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;

    for (const url of urls) {
      try {
        console.log(`Tentando: ${url}`);
        const response = await fetch(url, { headers });
        if (response.ok) {
          const data = await response.json();
          console.log(`Sucesso com ${url}:`, data);
          return data;
        }
      } catch (error) {
        console.log(`Falha com ${url}:`, error.message);
      }
    }
    
    throw new Error('Todas as URLs falharam');
  };

  useEffect(() => {
    const buscarOportunidades = async () => {
      setLoading(true);
      try {
        const dados = await apiCall('Oportunidade');
        console.log('Dados recebidos da API:', dados);

        setOportunidades(dados);
        setOportunidadesFiltradas(dados); // Inicializa as oportunidades filtradas com todas as oportunidades
      } catch (error) {
        console.error('Erro ao carregar oportunidades:', error);
        setOportunidades([]);
        setOportunidadesFiltradas([]);
      } finally {
        setLoading(false);
      }
    };

    buscarOportunidades();
  }, []);

  useEffect(() => {
    if (idCliente) {
      setValorFiltro(idCliente); // Atualiza o valor do filtro com o idCliente da URL
      setFiltro('clienteId');    // Define o filtro como 'clienteId'
      // Filtra as oportunidades com base no idCliente
      const filtradas = oportunidades.filter((oportunidade) =>
        (oportunidade.clienteId || oportunidade.ClienteId || '').toString().includes(idCliente)
      );
      setOportunidadesFiltradas(filtradas);
    }
  }, [idCliente, oportunidades]); // Reexecuta o efeito quando idCliente ou oportunidades mudam

  useEffect(() => {
    // Busca todos os clientes cadastrados na API de clientes
    const buscarClientes = async () => {
      setLoading(true);
      try {
        const dados = await apiCall('Cliente');
        setClientes(dados);
      } catch (error) {
        console.error('Erro ao carregar clientes:', error);
        setClientes([]);
      } finally {
        setLoading(false);
      }
    };
    
    buscarClientes();
  }, []);

  useEffect(() => {
    const handler = () => {
      setMostrarPopupCliente(false);
      setPopupNovaOportunidade(null);
      setPopupEditarOportunidade(null);
    };
    window.addEventListener('oportunidades-fechar-todos-popups', handler);
    return () => window.removeEventListener('oportunidades-fechar-todos-popups', handler);
  }, []);

  const handleFiltroChange = (novoFiltro) => {
    setFiltro(novoFiltro);
    setValorFiltro(''); // Limpa o valor do input ao mudar o filtro
  };

  const handleInputChange = (e) => {
    const valor = e.target.value;
    setValorFiltro(valor);

    // Filtra as oportunidades dinamicamente com compatibilidade de campos
    const filtradas = oportunidades.filter((oportunidade) => {
      if (filtro === 'clienteId') {
        const clienteId = oportunidade.clienteId || oportunidade.ClienteId || '';
        return String(clienteId).includes(valor);
      }
      if (filtro === 'id') {
        const id = oportunidade.id || oportunidade.Id || '';
        return String(id).includes(valor);
      }
      if (filtro === 'clienteNome') {
        const clienteNome = oportunidade.clienteNome || oportunidade.ClienteNome || '';
        return clienteNome.toLowerCase().includes(valor.toLowerCase());
      }
      if (filtro === 'status') {
        const status = oportunidade.status || oportunidade.Status || '';
        return status.toLowerCase().includes(valor.toLowerCase());
      }
      if (filtro === 'titulo') {
        const titulo = oportunidade.oportunidade_Titulo || oportunidade.titulo || oportunidade.Titulo || '';
        return titulo.toLowerCase().includes(valor.toLowerCase());
      }
      return true;
    });

    setOportunidadesFiltradas(filtradas);
  };

  return (
    <div className="container-geral fundo-branco">
      <LoadingOverlay isLoading={loading} message="Carregando oportunidades..." />
      {/* Botão Nova Oportunidade */}
      <div style={{ width: '100%', display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
        <button
          className="btn-nova-oportunidade"
          style={{
            background: 'rgb(235, 133, 0)',
            color: 'white',
            border: 'none',
            borderRadius: 6,
            padding: '16px 32px',
            fontSize: 20,
            fontWeight: 'bold',
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            transition: 'background 0.2s'
          }}
          onMouseEnter={e => e.target.style.background = 'rgb(208, 116, 0)'}
          onMouseLeave={e => e.target.style.background = 'rgb(235, 133, 0)'}
          onClick={() => setMostrarPopupCliente(true)}
        >
          Nova Oportunidade
        </button>
        <MobileAddListener onOpen={() => setMostrarPopupCliente(true)} />
      </div>

      {/* Popup de escolha de cliente */}
      {mostrarPopupCliente && (
        <div className="popup-agendamento-overlay" onClick={() => setMostrarPopupCliente(false)}>
          <div className="popup-agendamento" onClick={e => e.stopPropagation()} style={{ position: 'relative' }}>
            <button
              className="popup-fechar-btn"
              onClick={() => setMostrarPopupCliente(false)}
              aria-label="Fechar"
            >
              ×
            </button>
            <div className="popup-agendamento-centro">
              <div className="popup-titulo-laranja">Escolha um Cliente</div>
              <div className="popup-lista-clientes">
                {clientes.length === 0 && (
                  <div className="popup-card-cliente vazio">Nenhum cliente cadastrado.</div>
                )}
                {clientes.map(cliente => (
                  <div className="popup-card-cliente" key={cliente.id || cliente.Id}>
                    <div className="popup-card-info">
                      <span className="popup-card-id">ID: {cliente.id || cliente.Id}</span>
                      <span className="popup-card-nome">CPF: {cliente.cpfCnpj || cliente.cpF_CNPJ || ''}</span>
                      <span className="popup-card-nome">{cliente.nome || cliente.Nome}</span>
                    </div>
                    <button
                      className="botao-adicionar-oportunidade"
                      style={{
                        background: 'rgb(235, 133, 0)',
                        color: 'white',
                        border: 'none',
                        borderRadius: 6,
                        padding: '10px 22px',
                        fontSize: 16,
                        fontWeight: 600,
                        cursor: 'pointer',
                        marginLeft: 18,
                        transition: 'background 0.2s'
                      }}
                      onMouseEnter={e => e.target.style.background = 'rgb(208, 116, 0)'}
                      onMouseLeave={e => e.target.style.background = 'rgb(235, 133, 0)'}
                      onClick={() => setPopupNovaOportunidade(cliente)}
                    >
                      Adicionar Nova Oportunidade
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
      {popupNovaOportunidade && (
        <PopupNovaOportunidade
          cliente={popupNovaOportunidade}
          onClose={() => setPopupNovaOportunidade(null)}
        />
      )}
      {popupEditarOportunidade && (
        <PopupEditarOportunidade
          oportunidadeId={popupEditarOportunidade}
          onClose={() => setPopupEditarOportunidade(null)}
        />
      )}

      {/* Campo de filtro */}
      <div className="filtro-container">
        <label className="filtro-label">Filtrar por:</label>
        <div className="filtro-input-container">
          <div className="filtro-input-wrapper">
            <input
              type="text"
              className="filtro-input"
              placeholder={`Filtrar por ${
                filtro === 'clienteId' ? 'ID do Cliente' :
                filtro === 'id' ? 'ID da Oportunidade' :
                filtro === 'clienteNome' ? 'Nome do Cliente' :
                filtro === 'titulo' ? 'Título' :
                filtro === 'status' ? 'Status' : filtro
              }`}
              value={valorFiltro}
              onChange={handleInputChange}
            />
            {valorFiltro && (
              <button
                className="filtro-clear-button"
                onClick={() => setValorFiltro('')}
                title="Limpar filtro"
              >
                ×
              </button>
            )}
          </div>
          <div className="filtro-custom-select">
            <button
              className="filtro-custom-botao"
              onClick={() => setDropdownAberto(!dropdownAberto)}
            >
              {`Filtrar por ${
                filtro === 'clienteId' ? 'ID do Cliente' :
                filtro === 'id' ? 'ID da Oportunidade' :
                filtro === 'clienteNome' ? 'Nome do Cliente' :
                filtro === 'titulo' ? 'Título' :
                filtro === 'status' ? 'Status' : filtro
              }`}
              <span className="seta-para-baixo">▼</span>
            </button>
            {dropdownAberto && (
              <div className="filtro-custom-opcoes">
                <div onClick={() => handleFiltroChange('clienteId')}>Filtrar por ID do Cliente</div>
                <div onClick={() => handleFiltroChange('id')}>Filtrar por ID da Oportunidade</div>
                <div onClick={() => handleFiltroChange('clienteNome')}>Filtrar por Nome do Cliente</div>
                <div onClick={() => handleFiltroChange('titulo')}>Filtrar por Título</div>
                <div onClick={() => handleFiltroChange('status')}>Filtrar por Status</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Lista de oportunidades filtradas */}
      <div className="oportunidades-cards-container">
        {oportunidadesFiltradas.map((oportunidade) => {
          const nomeCliente = oportunidade.clienteNome || oportunidade.ClienteNome || 'N/A';
          const titulo = oportunidade.oportunidade_Titulo || oportunidade.titulo || oportunidade.Titulo || 'Sem título';
          const status = oportunidade.status || oportunidade.Status || 'N/A';
          const descricao = oportunidade.descricao || oportunidade.Descricao || 'N/A';
          const dataCriacao = oportunidade.dataCriacao || oportunidade.DataCriacao || 'N/A';
          const clienteId = oportunidade.clienteId || oportunidade.ClienteId || 'N/A';
          const oportunidadeId = oportunidade.id || oportunidade.Id || 'N/A';

          return (
            <div key={oportunidadeId} className="oportunidade-card" style={{borderLeft: '2.5px solid rgb(235, 133, 0)'}}>
              <div className="oportunidade-card-content">
                <div className="oportunidade-card-fields">
                  <div className="oportunidade-field oportunidade-field-id-oportunidade">
                    <label className="oportunidade-field-label">ID Oportunidade:</label>
                    <span className="oportunidade-field-value">{oportunidadeId}</span>
                  </div>
                  
                  <div className="oportunidade-field oportunidade-field-id-cliente">
                    <label className="oportunidade-field-label">ID do Cliente:</label>
                    <span className="oportunidade-field-value">{clienteId}</span>
                  </div>
                  
                  <div className="oportunidade-field oportunidade-field-nome-cliente">
                    <label className="oportunidade-field-label">Nome Cliente:</label>
                    <span className="oportunidade-field-value">{nomeCliente}</span>
                  </div>
                  
                  <div className="oportunidade-field oportunidade-field-titulo">
                    <label className="oportunidade-field-label">Título:</label>
                    <span className="oportunidade-field-value">{titulo}</span>
                  </div>
                  
                  <div className="oportunidade-field oportunidade-field-status">
                    <label className="oportunidade-field-label">Status:</label>
                    <span className="oportunidade-field-value">{status}</span>
                  </div>
                  
                  <div className="oportunidade-field oportunidade-field-data">
                    <label className="oportunidade-field-label">Data Criação:</label>
                    <span className="oportunidade-field-value">{dataCriacao}</span>
                  </div>
                </div>
              </div>
              
              <div className="oportunidade-card-actions">
                <button
                  className="btn-editar-oportunidade"
                  onClick={() => setPopupEditarOportunidade(oportunidadeId)}
                  title="Editar oportunidade"
                >
                  Editar
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default OportunidadeDropdown;